import { GridApi } from '@ag-grid-community/core';
import {
  Button,
  ButtonMode,
  ButtonSize,
  Checkbox,
  DataLoadError,
  Divider,
  Icon,
  ICONS,
  Loader,
  ToasterMode,
  Tooltip,
} from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import isEmpty from 'lodash/isEmpty';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Pluralize from 'react-pluralize';
import { useQueryClient } from 'react-query';
import { Link, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { EXPORT_EXCEL_SHEET_NAME, GENERIC_ERROR_TEXT, ROUTES } from '../../../../Base.constants';
import { MODAL_TYPES, useGlobalModalContext } from '../../../../components/GlobalModal';
import { useFetchIssuers } from '../../../../hooks/issuers';
import useFetchUserPermissions from '../../../../hooks/permissions/useFetchUserPermissions';
import useFetchUserProfile from '../../../../hooks/profile/useFetchUserProfile';
import useTableQueryParams from '../../../../hooks/table/useTableQueryParams';
import { useToast } from '../../../../hooks/toast/useToast';
import { IssuerListDto, IssuerListItemDto } from '../../../../services/dtos/issuer/issuerList.dto';
import { IssuersQueryParams } from '../../../../services/issuers/issuersQueryParams';
import { formatCommonDate } from '../../../../utils/dateUtils';
import { isNotFound, navigateToCompaniesList } from '../../../../utils/navigationUtils';
import { hasPermissionToRp, hasShareCopyWatchlist } from '../../../../utils/permissionsUtils';
import { processCellForExport } from '../../../Companies/Companies.logic';
import NotFound from '../../../NotFound';
import WatchlistName from '../WatchlistName/WatchlistName';
import { WatchlistsOutletContext } from '../WatchlistsTab';
import { isNewWatchlist } from '../WatchlistsTab.logic';
import WatchlistTable from '../WatchlistTable/WatchlistTable';
import {
  DISPLAY_NAME_COL_ID,
  TICKER_COL_ID,
  WATCHLIST_TABLE_PAGE_SIZE,
} from '../WatchlistTable/WatchlistTable.constants';
import { WatchListTableContextProvider } from '../WatchlistTable/WatchlistTableContext';
import AddIssuerPopup from './AddIssuerPopup';
import AlertPopup from './AlertPopup/AlertPopup';
import {
  createExportFileName,
  fetchAllPagesForExport,
  getIsUserLimitExceeded,
  getTotalTagsCount,
  mapExtraPagesForExport,
} from './Watchlist.logic';
import styles from './Watchlist.module.scss';
import { trackRiskProductsAlerts } from '../../../../utils/trackUsageUtils';

const cx = classNames.bind(styles);

const Watchlist = (): React.ReactElement | null => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: profile } = useFetchUserProfile();
  const [issuersToRemove, setIssuersToRemove] = useState<string[]>([]);
  const [isAlertPopupOpen, setIsAlertPopupOpen] = useState<boolean>(false);
  const [isNameBeingChanged, setIsNameBeingChanged] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [allIssuersPages, setAllIssuersPages] = useState<IssuerListDto[]>();
  const { showModal, closeModal, setError } = useGlobalModalContext();
  const { id = '' } = useParams<string>();
  const { data: userPermissions } = useFetchUserPermissions();
  const gridApiRef = useRef<GridApi | null>(null);
  const { page, sort, onSortChanged, onPageChanged } = useTableQueryParams();

  const queryParams: IssuersQueryParams = {
    skip: (page - 1) * WATCHLIST_TABLE_PAGE_SIZE,
    take: WATCHLIST_TABLE_PAGE_SIZE,
    watchlistId: id,
    onlyActive: false,
    ...sort,
  };

  const { data, isLoading, isFetching, isRefetching, isError, error, refetch } = useFetchIssuers(queryParams, {
    keepPreviousData: true,
    onSuccess: (data) => {
      if (!(data.results || []).length && page > 1) {
        onPageChanged(page - 1);
      }
    },
  });

  const issuers: IssuerListItemDto[] = useMemo(() => {
    const currentPage = data?.results ?? [];
    if (allIssuersPages && !isEmpty(allIssuersPages)) {
      const previousPages = allIssuersPages
        .slice(0, page - 1)
        .map(mapExtraPagesForExport)
        .flat();
      const nextPages = allIssuersPages.slice(page).map(mapExtraPagesForExport).flat();
      return [...previousPages, ...currentPage, ...nextPages];
    }
    return currentPage;
  }, [data, allIssuersPages, page]);

  useEffect(() => {
    if (isExporting && allIssuersPages) {
      exportDataToExcel();
      setIsExporting(false);
      setAllIssuersPages(undefined);
    }
  }, [isExporting, allIssuersPages]);

  const isIssuersLoading = isLoading || isFetching;
  const totalResults = data?.totalResults || 0;

  const {
    watchlists,
    newEmptyWatchlist,
    onWatchlistUpdate,
    isLoading: isAnyWatchlistLoading,
    isWatchlistNameChanging,
  } = useOutletContext<WatchlistsOutletContext>();
  const { showToast } = useToast();

  const openedWatchlist = watchlists?.find((watchlist) =>
    isNewWatchlist(id) ? watchlist.id === newEmptyWatchlist?.id : watchlist.id === id
  );

  const isShareWatchlist: boolean = openedWatchlist?.ownerId !== profile?.userId;

  const watchlistsNames = watchlists
    ?.filter((watchlist) => watchlist.id !== openedWatchlist?.id && watchlist)
    ?.filter((watchlist) => watchlist.ownerId === profile?.userId)
    .map((watchlist) => watchlist.name);

  const isUserLimitExceeded = getIsUserLimitExceeded(watchlists);
  const isDisabled = isFetching || isNameBeingChanged;
  const removeCount = issuersToRemove.length > 0 ? `(${issuersToRemove.length})` : '';
  const isAlertDisabled = isDisabled || !data?.totalResults;

  const handleIsAlertPopupOpen = useCallback(
    (toggle: boolean) => {
      if (!isAlertDisabled) {
        setIsAlertPopupOpen(toggle);
      }
    },
    [isAlertDisabled]
  );

  const onRemoveSuccess = () => {
    closeModal();
    setIssuersToRemove([]);
    showToast(issuersToRemove.length > 1 ? 'Your issuers have been removed' : 'Your issuer has been removed');
  };

  const onRemoveIssuers = useCallback(() => {
    showModal(MODAL_TYPES.CONFIRM_REMOVE_ISSUERS, {
      isMultipleIssuers: issuersToRemove.length > 1,
      onConfirm: () => {
        if (!openedWatchlist) {
          return;
        }
        onWatchlistUpdate(
          {
            id: openedWatchlist.id,
            watchList: {
              tags: openedWatchlist.tags.filter((tag) => !issuersToRemove.includes(tag)),
            },
          },
          {
            onSuccess: onRemoveSuccess,
            onError: () => setError(GENERIC_ERROR_TEXT),
          }
        );
      },
    });
  }, [onWatchlistUpdate, showModal, issuersToRemove, openedWatchlist]);

  const onAddIssuersWithCompaniesTool = () => {
    navigateToCompaniesList(userPermissions, navigate);
  };

  const onUploadIssuers = () => {
    showModal(MODAL_TYPES.UPLOAD_ISSUERS, {
      watchList: openedWatchlist,
      totalTagsCount: getTotalTagsCount(watchlists),
    });
  };

  const toggleMyFeed = (_, data) => {
    if (openedWatchlist) {
      onWatchlistUpdate({
        id: openedWatchlist.id,
        watchList: {
          myCS: data.checked,
        },
      });
    }
  };

  const onShare = () => {
    showModal(MODAL_TYPES.SHARE_WATCHLIST, {
      watchList: openedWatchlist,
    });
  };

  const onCopy = () => {
    showModal(MODAL_TYPES.COPY_WATCHLIST, {
      name: openedWatchlist?.name,
      tags: openedWatchlist?.tags || [],
    });
  };

  const onExport = async () => {
    setIsExporting(true);
    try {
      const allPages = await fetchAllPagesForExport(queryClient, totalResults, queryParams);
      setAllIssuersPages(allPages);
    } catch (er) {
      showToast(GENERIC_ERROR_TEXT, ToasterMode.ERROR);
      setIsExporting(false);
      setAllIssuersPages(undefined);
    }
  };

  const exportDataToExcel = () => {
    gridApiRef.current?.exportDataAsExcel({
      processCellCallback: processCellForExport,
      fileName: createExportFileName(),
      sheetName: EXPORT_EXCEL_SHEET_NAME,
      columnKeys: [DISPLAY_NAME_COL_ID, TICKER_COL_ID],
      exportedRows: 'all',
    });
  };

  const updateWatchlist = (watchlist) => {
    if (openedWatchlist) {
      onWatchlistUpdate({
        id: openedWatchlist.id,
        watchList: watchlist,
      });
    }
  };

  const selectRiskProductAlert = (alert) => {
    trackRiskProductsAlerts(alert, id);
    updateWatchlist({
      riskProductAlert: alert,
    });
  };

  useEffect(() => {
    setIsNameBeingChanged(isWatchlistNameChanging);
  }, [isWatchlistNameChanging]);

  const isCustomizable = useMemo(() => openedWatchlist?.ownerId === profile?.userId, [openedWatchlist, profile]);

  const renderWatchlist = () => {
    // 400 is returned in case invalid watchlist id was provided in the url, therefore there's no point retrying it.
    // Instead, show page not found
    if (isError && !isNotFound(error)) {
      return <DataLoadError onRetryClick={refetch} />;
    }

    if (isAnyWatchlistLoading) {
      return <Loader position="center" />;
    }

    if (!openedWatchlist) {
      return <NotFound showNavigateToFeeds={false} />;
    }

    return (
      <>
        <header className={cx('header')}>
          <WatchlistName
            watchlist={openedWatchlist}
            watchlistsNames={watchlistsNames}
            isNewWatchlist={isNewWatchlist(openedWatchlist.id)}
            onActiveChanged={setIsNameBeingChanged}
          />
          <div className={cx('subheader')}>
            <div className={cx('summary-container')}>
              <div className={cx('summary')}>
                <div className={cx('summary-item')} data-testid="watchlist-issuers-count">
                  <Pluralize count={totalResults || 0} singular="Issuer" plural="Issuers" />
                </div>
                <div className={cx('summary-item')}>
                  Last Edited {formatCommonDate(openedWatchlist.updatedAt, true)}
                </div>
              </div>

              {!isShareWatchlist && (
                <div className={cx('toolbar-section')}>
                  <Divider className={cx('divider')} vertical />
                  <Checkbox
                    className={cx('my-feed-toggle')}
                    dataTestId="my-feed-toggle"
                    onClick={toggleMyFeed}
                    readOnly={isNameBeingChanged}
                    disabled={isNameBeingChanged}
                    checked={openedWatchlist.myCS}
                    label="My Feed"
                    aria-label="My Feed"
                    toggle
                  />
                </div>
              )}
            </div>
            <div className={cx('toolbar')}>
              {!isShareWatchlist && (
                <div className={cx('toolbar-section')}>
                  <Tooltip
                    disabled={!!issuersToRemove.length || isNameBeingChanged}
                    content="Select issuer(s) to delete"
                    data-testid="delete-issuers-tooltip"
                  >
                    <div>
                      <Button
                        ariaLabel={`Delete Selected ${removeCount}`}
                        disabled={!issuersToRemove.length || isNameBeingChanged}
                        mode={ButtonMode.TERTIARY}
                        onClick={onRemoveIssuers}
                      >
                        <Icon name={ICONS.Delete} />
                        Delete Selected {removeCount}
                      </Button>
                    </div>
                  </Tooltip>
                </div>
              )}
              <div className={cx('toolbar-section')}>
                {!isShareWatchlist && hasShareCopyWatchlist(userPermissions) && (
                  <>
                    <Divider className={cx('divider')} vertical />
                    <Tooltip
                      content="Share/Unshare Watchlist"
                      trigger={
                        <span className={cx('button-wrapper')}>
                          <Button
                            ariaLabel="Share"
                            mode={ButtonMode.TERTIARY}
                            size={ButtonSize.SMALL}
                            onClick={onShare}
                            dataTestId="share-table-button"
                          >
                            <Icon name={openedWatchlist?.sharedWithUsers?.length ? ICONS.ShareFilled : ICONS.Share} />
                          </Button>
                        </span>
                      }
                    />
                  </>
                )}
                {hasShareCopyWatchlist(userPermissions) && (
                  <Tooltip
                    content="Duplicate Watchlist"
                    trigger={
                      <span className={cx('button-wrapper')}>
                        <Button
                          ariaLabel="Copy"
                          mode={ButtonMode.TERTIARY}
                          size={ButtonSize.SMALL}
                          onClick={onCopy}
                          dataTestId="copy-table-button"
                        >
                          <Icon name={ICONS.Copy} />
                        </Button>
                      </span>
                    }
                  />
                )}
                <Tooltip
                  content="Export Watchlist"
                  disabled={isDisabled}
                  trigger={
                    <span className={cx('button-wrapper')}>
                      <Button
                        ariaLabel="Export"
                        mode={ButtonMode.TERTIARY}
                        size={ButtonSize.SMALL}
                        onClick={onExport}
                        dataTestId="export-table-button"
                        disabled={!data?.results.length || isDisabled}
                        loading={isExporting}
                      >
                        <Icon name={ICONS.Download} />
                      </Button>
                    </span>
                  }
                />
                {hasPermissionToRp(userPermissions) && (
                  <Tooltip
                    content="Open Watchlist in Dashboards"
                    disabled={isNameBeingChanged}
                    trigger={
                      <Link
                        aria-label="Dashboard Link"
                        className={cx('dashboard-link', {
                          'is-disabled': isDisabled,
                        })}
                        to={ROUTES.dashboards(openedWatchlist.id)}
                      >
                        <Icon name={ICONS.Dashboard} dataTestId="dasboard-link" />
                      </Link>
                    }
                  />
                )}
                {!isShareWatchlist && (
                  <AlertPopup
                    watchlist={openedWatchlist}
                    selectEmailAlert={updateWatchlist}
                    isOpen={isAlertPopupOpen}
                    setIsOpen={handleIsAlertPopupOpen}
                    isDisabled={isAlertDisabled}
                    riskProductAlert={openedWatchlist.riskProductAlert}
                    selectRiskProductAlert={selectRiskProductAlert}
                  />
                )}
              </div>
              {!isShareWatchlist && (
                <div className={cx('toolbar-section')}>
                  <Divider className={cx('divider')} vertical />
                  <AddIssuerPopup
                    openedWatchList={openedWatchlist}
                    watchLists={watchlists}
                    isDisabled={isNameBeingChanged}
                  />
                </div>
              )}
            </div>
          </div>
        </header>
        <div className={cx('content')}>
          <WatchListTableContextProvider isNameBeingChanged={isNameBeingChanged}>
            <WatchlistTable
              id={id}
              selectable={isCustomizable}
              issuers={issuers}
              isIssuersLoading={isIssuersLoading || isRefetching}
              onSortChanged={onSortChanged}
              page={page}
              onPageChanged={onPageChanged}
              totalResults={totalResults}
              sort={sort}
              onIssuerSelection={setIssuersToRemove}
              noDataComponentParams={{
                isUserLimitExceeded,
                onUploadIssuers,
                onAddIssuersWithCompaniesTool,
              }}
              gridApiRef={gridApiRef}
            />
          </WatchListTableContextProvider>
        </div>
      </>
    );
  };

  return <div className={cx('body')}>{renderWatchlist()}</div>;
};

export default Watchlist;
